import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { translate } from '../../../services/translate';
import { AgentIsNotOnTeam, appAgentService } from '../../../services/central-api/app-agent';
import DrawnPlate from '../../../components/DrawnPlate';
import MyAppText from '../../../components/MyAppText';
import Divider from '../../../components/Divider';
import LprEventAlertModal from './LprEventAlertModal';
import moment, { Moment } from 'moment';
import Toast from 'react-native-toast-message';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useQuery } from '@tanstack/react-query';

export default function LprEvents() {

    const { styles, theme } = useStyles(styleSheet);
    const [events, setEvents] = useState<LprDetection[]>([]);
    const [alertEvent, setAlertEvent] = useState<LprDetection>();
    const [intervalTime, setIntervalTime] = useState<number>(5000);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [lastShownAlert, setLastShownAlert] = useState<Moment>(moment());
    const [lprServiceRunning, setLprServiceRunning] = useState<boolean>(false);

    const isFocused = useIsFocused();

    useFocusEffect(useCallback(() => {
        if (!isFocused) {
            return;
        }
        window.getServicesStatus = (locationServiceRunning: string, lprServiceRunning: string) => {
            setLprServiceRunning(lprServiceRunning === 'true');
        };
        if (window.AndroidBridge?.replyServiceStatus) {
            window.AndroidBridge.replyServiceStatus();
        }
    }, [isFocused, window.AndroidBridge]));

    const isEnabled = isFocused && lprServiceRunning;

    const lprEventsQuery = useQuery({
        queryKey: ['lprEvents', lastShownAlert],
        queryFn: () => appAgentService.getOwnTeamDetections(),
        enabled: isEnabled,
        refetchInterval: intervalTime,
    });

    useEffect(() => {
        if (!lprEventsQuery.data) {
            return;
        }

        const res = lprEventsQuery.data;
        setEvents(res.responseList);
        setIntervalTime(res.refreshInterval);

        const alertEvent = res.responseList.find(
            d => d.description && moment(d.time).isAfter(lastShownAlert)
        );

        if (alertEvent && moment(alertEvent.time).isAfter(lastShownAlert)) {
            setAlertEvent(alertEvent);
            setIsModalVisible(true);
            setLastShownAlert(moment(alertEvent.time));
        }
    }, [lprEventsQuery.data, lastShownAlert]);

    useEffect(() => {
        if (!lprEventsQuery.error) {
            return;
        }
        if (lprEventsQuery.error instanceof AgentIsNotOnTeam) {
            Toast.show({
                type: 'sentinelxWarning',
                text1: translate('agentIsNotOnTeam'),
            });
        } else {
            console.error(lprEventsQuery.error);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }, [lprEventsQuery.error]);

    return (
        <View style={styles.container}>
            <View style={styles.card}>
                {
                    !lprServiceRunning ?
                        <View style={styles.serviceOff}>
                            <FontAwesomeIcon fontSize={30} icon={faExclamationTriangle as IconDefinition} color={theme.colors.warnings['500']} />
                            <MyAppText>{translate('lprServiceNotRunning')}</MyAppText>
                        </View> :
                        <>
                            <View style={{ rowGap: 7 }}>
                                <View style={styles.header}>
                                    <MyAppText style={styles.headerCell}>{translate('events')}</MyAppText>
                                </View>
                                <Divider thickness={1} color={theme.colors.neutral['300']} />
                            </View>
                            <ScrollView contentContainerStyle={{ gap: 10 }}>
                                <View>
                                    <ActivityIndicator size='small' color={theme.colors.loadingColor} />
                                </View>
                                {events.length ?
                                    events.map((event) => (
                                        <View key={event.id}>
                                            <DrawnPlate plate={event.plate} isAlert={event.description !== null} />
                                            <Divider thickness={1} color={theme.colors.neutral['300']} />
                                        </View>
                                    ))
                                    : null
                                }
                            </ScrollView>
                        </>
                }
            </View>
            {alertEvent &&
                <LprEventAlertModal
                    detectionId={alertEvent.id}
                    setIsModalVisible={setIsModalVisible}
                    isModalVisible={isModalVisible}
                />}

        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flex: 1,
        rowGap: 10
    },
    card: {
        borderWidth: 1,
        borderRadius: 8,
        borderColor: theme.colors.card.border,
        padding: 8,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.colors.card.background,
        height: 80,
        flex: 1,
        gap: 5,
    },
    header: {
        flexDirection: 'row',
    },
    headerCell: {
        fontSize: 14,
        color: theme.colors.labelColor
    },
    serviceOff: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 10
    }
}));
